import React from 'react';
import {
  Text,
  Title,
  Stack,
  Group,
  Badge,
  Alert,
  Divider,
  Box,
  rem,
} from '@mantine/core';
import { IconWeight, IconGift } from '@tabler/icons-react';
import { formatPrice } from '../utils/formatPrice';
import { formatWeight } from '../utils/formatWeight';
import { offerLabel } from '../utils/offerLabel';

export default function ProductDetailInfo({
  product,
  selectedVariant,
  productName,
  displayPrice,
  displayOriginal,
  hasSpecialPrice,
  vatExclusive,
  discountPct,
  isSoldOut,
  t,
}) {
  const weightText =
    selectedVariant?.weight_value != null
      ? formatWeight(selectedVariant.weight_value, selectedVariant.weight_unit)
      : null;

  return (
    <Stack gap="xl">
      <Box>
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
          <Title order={1} size={rem(42)} fw={900} lh={1.1}>
            {productName}
          </Title>
          {isSoldOut ? <Badge color="red" size="lg" variant="filled">{t('soldOut')}</Badge> : null}
        </Group>

        {product.is_variable_weight && (
          <Alert
            icon={<IconWeight size={20} aria-hidden="true" />}
            title={t('variableWeightBannerTitle', 'Sold by weight')}
            color="yellow"
            radius="md"
            variant="light"
            mt="md"
            role="note"
          >
            <Text size="sm" lh={1.5}>
              {t(
                'variableWeightBanner',
                'This product is sold by weight. The final weight delivered may differ slightly from the quantity ordered. The actual price will be adjusted accordingly.'
              )}
            </Text>
          </Alert>
        )}

        {!product.is_bulk_only && displayPrice != null && (
          <Stack gap={4} mt="md">
            <Group align="center" gap="xs" wrap="wrap">
              <Group align="baseline" gap={6}>
                <Text size={rem(32)} fw={800} color={hasSpecialPrice ? "red.7" : "blue.7"}>
                  {formatPrice(displayPrice)}
                </Text>
                <Text size="sm" c="dimmed" fw={500}>
                  {vatExclusive ? t('exclVAT', 'excl. VAT') : t('inclVAT', 'incl. VAT')}
                </Text>
              </Group>
              {hasSpecialPrice && displayOriginal && (
                <Text size="lg" td="line-through" c="dimmed">
                  {formatPrice(displayOriginal)}
                </Text>
              )}
              {hasSpecialPrice && (
                <Badge color="green" variant="filled" size="lg">
                  {t('yourPrice', 'Your Price')}
                </Badge>
              )}
            </Group>
            {hasSpecialPrice && displayOriginal && displayPrice && (
              <Text size="sm" fw={600} color="green.7">
                {t('youSave', 'You save:')} {discountPct}%
              </Text>
            )}
          </Stack>
        )}

        {weightText && (
          <Text size="sm" c="dimmed" mt={4}>
            {weightText}
          </Text>
        )}
      </Box>

      {selectedVariant?.offer && (
        <Alert
          icon={<IconGift size={20} aria-hidden="true" />}
          title={offerLabel(selectedVariant.offer, t)}
          color="teal"
          radius="md"
          variant="light"
          role="note"
        >
          <Text size="sm" lh={1.5}>
            {t('productDetailOfferHint', 'Applies automatically at checkout when the offer conditions are met.')}
          </Text>
        </Alert>
      )}

      <Divider />
    </Stack>
  );
}
